/**
 * Từ vựng nghiệp vụ cho Bản đồ GIS và Biên tập vị trí (SCR-034 / SCR-035 — S12.5).
 *
 * ⚠️ LỊCH SỬ — ĐỌC TRƯỚC KHI SỬA: bản đầu của file này khai từ vựng theo *contract*
 * `getCemeteryMap` (`contracts/openapi/ncc-api.yaml`), với ghi chú "tiêu thụ trung thực enum
 * từ contract". Nhưng ba enum trong contract đó **không khớp một giá trị nào dưới DB**:
 *
 *     contract khai                     DB thật (đo 17/09/2026)
 *     ────────────────────────────────  ──────────────────────────────────────
 *     IDENTIFIED·UNIDENTIFIED·SUSPECTED KNOWN·PARTIAL·UNKNOWN
 *     EMPTY·OCCUPIED·RESERVED·LOCKED    AVAILABLE·OCCUPIED·CLOSED
 *     INTACT·DAMAGED·COLLAPSED·UNKNOWN  chuỗi tiếng Việt tự do ("Nguyên trạng", …)
 *
 * Hậu quả thật đã đo được: `GisMapPage` lọc `identityFilters[props.identityStatus]` với
 * `identityStatus = "KNOWN"` ⇒ `undefined` ⇒ **431/432 ô mộ bị loại oan**, bản đồ gần như trống.
 * Vì vậy từ vựng ở đây bám **DB**, không bám contract. Contract sẽ được nắn theo bằng CR riêng
 * (nợ đã ghi trong plan); khi nào CR đó xong thì hai bên mới khớp lại.
 */
import { label } from "../../../lib/labels";

export type GisTone = "info" | "success" | "warning" | "danger" | "neutral";

/** Khớp CHECK `ck_position_state` của V16: AVAILABLE | OCCUPIED | CLOSED. */
export type GisPositionState = "AVAILABLE" | "OCCUPIED" | "CLOSED";
/** Khớp `cemetery.grave.identity_state` và ba khoá đóng của `CemeteryGraveStats.identityCounts`. */
export type GisIdentityStatus = "KNOWN" | "PARTIAL" | "UNKNOWN";
export type GisLayerCode = "CEMETERY_BOUNDARY" | "SECTION" | "GRAVE_POSITION";

export const GIS_POSITION_STATE_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: GisTone }>>
> = Object.freeze({
  AVAILABLE: { label: "Còn trống", tone: "neutral" },
  OCCUPIED: { label: "Đang an táng", tone: "success" },
  CLOSED: { label: "Đã đóng", tone: "danger" },
});

export function gisPositionStateLabel(code: string | undefined, displayName?: unknown): string {
  if (!code) return "—";
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return GIS_POSITION_STATE_LABELS[code]?.label ?? label(code, displayName);
}

export function gisPositionStateTone(code: string | undefined): GisTone {
  if (!code) return "neutral";
  return GIS_POSITION_STATE_LABELS[code]?.tone ?? "neutral";
}

/**
 * Nhãn ngắn dùng cho control hẹp (hộp kiểm lọc, chip). Vẫn là CÙNG ba khoá với bảng trên —
 * chỉ khác độ dài câu, không phải một từ vựng thứ hai.
 */
export const GIS_IDENTITY_STATUS_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: GisTone }>>
> = Object.freeze({
  KNOWN: { label: "Đã xác định", tone: "success" },
  PARTIAL: { label: "Một phần", tone: "warning" },
  UNKNOWN: { label: "Chưa xác định", tone: "neutral" },
});

export function gisIdentityStatusLabel(code: string | undefined, displayName?: unknown): string {
  if (!code) return "—";
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return GIS_IDENTITY_STATUS_LABELS[code]?.label ?? label(code, displayName);
}

export function gisIdentityStatusShortLabel(code: string | undefined, displayName?: unknown): string {
  if (!code) return "—";
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return GIS_IDENTITY_STATUS_LABELS[code]?.label ?? label(code, displayName);
}

export function gisIdentityStatusTone(code: string | undefined): GisTone {
  if (!code) return "neutral";
  return GIS_IDENTITY_STATUS_LABELS[code]?.tone ?? "neutral";
}

/**
 * Màu ô mộ theo danh tính — MỘT nguồn cho cả bốn chỗ dùng: nét vẽ trên canvas, chấm màu
 * trong hộp kiểm lọc, chấm trong rail chi tiết, và chú giải. Trước đây mỗi chỗ tự chép hex
 * một bản; đó đúng là cách một từ vựng trôi khỏi nguồn mà không ai thấy.
 */
export const GIS_IDENTITY_COLORS: Readonly<
  Record<string, Readonly<{ fill: string; stroke: string }>>
> = Object.freeze({
  KNOWN: { fill: "var(--ncc-color-status-success)", stroke: "var(--ncc-primitive-emerald-700)" },
  PARTIAL: { fill: "var(--ncc-color-status-warning)", stroke: "var(--ncc-primitive-amber-800)" },
  UNKNOWN: { fill: "var(--ncc-color-grave-unknown, var(--ncc-primitive-stone-slate))", stroke: "var(--ncc-color-text-secondary)" },
});

/** Danh tính trống hoặc lạ ⇒ màu trung tính, không bịa một mức danh tính thứ tư. */
export const GIS_IDENTITY_FALLBACK_COLOR: Readonly<{ fill: string; stroke: string }> =
  Object.freeze({ fill: "var(--ncc-color-grave-unknown, var(--ncc-primitive-stone-slate))", stroke: "var(--ncc-color-text-secondary)" });

export function gisIdentityColor(code: string | undefined): Readonly<{ fill: string; stroke: string }> {
  if (!code) return GIS_IDENTITY_FALLBACK_COLOR;
  return GIS_IDENTITY_COLORS[code] ?? GIS_IDENTITY_FALLBACK_COLOR;
}

/*
 * KHÔNG có `GIS_PHYSICAL_CONDITION_LABELS`.
 *
 * Bản trước khai INTACT/DAMAGED/COLLAPSED/UNKNOWN — cả bốn là hư cấu, chưa từng tồn tại ở đâu.
 * `cemetery.grave.physical_condition` là cột text tự do (V1 chỉ DEFAULT 'NORMAL', không CHECK);
 * dữ liệu thật đang là "Nguyên trạng", "Cần tu bổ nắp", "Bia mờ chữ". Nên nơi hiển thị in
 * NGUYÊN VĂN giá trị DB. Dựng lại bảng tra ở đây là bịa thêm một lần nữa.
 *
 * (Dữ liệu trôi — "Cần tu bổ"(1) vs "Cần tu bổ nắp"(141) — là việc của màn Chất lượng dữ liệu.)
 */

export function gisLayerLabel(code: string | undefined, displayName?: unknown): string {
  if (!code) return "—";
  return label(code, displayName);
}
